import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { GoogleLogo, List, X, CaretDown, Lightning, Crosshair, Scales, ChartBar, Crown, TrendUp, Users, ArrowsLeftRight, Trophy, BookOpen, Article, Newspaper } from "@phosphor-icons/react";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PromoBanner from "@/components/PromoBanner";

const FEATURE_ITEMS = [
  { to: "/features/trade-analyzer", label: "AI Trade Analyzer", desc: "AI-powered trade suggestions for your roster", icon: <Lightning size={16} weight="fill" /> },
  { to: "/features/trade-finder", label: "Trade Finder", desc: "Auto-scan your league for mutual-fit deals", icon: <Crosshair size={16} weight="fill" /> },
  { to: "/features/trade-simulator", label: "Trade Simulator", desc: "See how any trade changes both rosters", icon: <Scales size={16} weight="fill" /> },
  { to: "/features/roster-tools", label: "Roster Tools", desc: "Contender rankings, optimizer, all teams", icon: <ChartBar size={16} weight="fill" /> },
  { to: "/features/draft-room", label: "Draft War Room", desc: "Live draft assistant with real ADP data", icon: <Crown size={16} weight="fill" /> },
];

const TOOL_ITEMS = [
  { to: "/trade-calculator", label: "Trade Calculator", desc: "Evaluate any trade instantly — free", icon: <Scales size={16} weight="fill" /> },
  { to: "/rankings", label: "Dynasty Rankings", desc: "400+ players with daily-updated values", icon: <TrendUp size={16} weight="fill" /> },
  { to: "/players", label: "Player Directory", desc: "Full profiles, trades, championship rates", icon: <Users size={16} weight="fill" /> },
  { to: "/trades", label: "Trade Database", desc: "40,000+ real trades from Sleeper leagues", icon: <ArrowsLeftRight size={16} weight="fill" /> },
  { to: "/compare", label: "Player Comparisons", desc: "3,000+ head-to-head breakdowns", icon: <ChartBar size={16} weight="fill" /> },
];

const LEARN_ITEMS = [
  { to: "/research", label: "Research", desc: "33 original data articles from 6,976 champions", icon: <Trophy size={16} weight="fill" /> },
  { to: "/guide", label: "Dynasty Guides", desc: "Startup, trade deadline, rookie, rebuild", icon: <BookOpen size={16} weight="fill" /> },
  { to: "/blog", label: "Blog", desc: "Weekly analysis: Buy Low, Rookie Watch, Trade of the Week", icon: <Newspaper size={16} weight="fill" /> },
  { to: "/about", label: "About", desc: "Our data, methodology, and what makes us different", icon: <Article size={16} weight="fill" /> },
];

function NavDropdown({ label, items, isOpen, onToggle, onClose, dropdownRef, wide }) {
  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={onToggle} className="flex items-center gap-1 text-sm text-[#a3a3a3] hover:text-white transition-colors font-medium">
        {label} <CaretDown size={11} weight="bold" className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 ${wide ? "w-80" : "w-72"} border border-[#262626] bg-[#171717] rounded-sm shadow-2xl z-50 overflow-hidden transition-all duration-150 ${isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1 pointer-events-none"}`}>
        {items.map(f => (
          <Link key={f.to} to={f.to} onClick={onClose} className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-[#262626] transition-colors border-b border-[#1a1a1a] last:border-0" tabIndex={isOpen ? 0 : -1}>
            <span className="text-[#ccff00] mt-0.5 shrink-0">{f.icon}</span>
            <div>
              <p className="text-sm text-white font-semibold">{f.label}</p>
              <p className="text-[11px] text-[#666]">{f.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function PublicNav() {
  const hideNav = useIsAppNavVisible();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // "tools" | "features" | "learn" | null
  const toolsRef = useRef(null);
  const featuresRef = useRef(null);
  const learnRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target) &&
          featuresRef.current && !featuresRef.current.contains(e.target) &&
          learnRef.current && !learnRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (hideNav) return null;

  const handleSignIn = () => { window.location.href = `/api/auth/google/login`; };
  const toggle = (key) => setOpenDropdown(prev => prev === key ? null : key);
  const close = () => setOpenDropdown(null);

  return (
    <div className="border-b border-[#262626] sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm">
      <PromoBanner />
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center no-underline" onClick={() => { setMobileOpen(false); close(); }}>
          <img src="/brand/logo-wordmark-dark-transparent.png" alt="Dynasty Trade Maker" className="h-9 sm:h-11 w-auto" data-testid="site-logo" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6">
          <NavDropdown label="Tools" items={TOOL_ITEMS} isOpen={openDropdown === "tools"} onToggle={() => toggle("tools")} onClose={close} dropdownRef={toolsRef} />
          <NavDropdown label="Features" items={FEATURE_ITEMS} isOpen={openDropdown === "features"} onToggle={() => toggle("features")} onClose={close} dropdownRef={featuresRef} />
          <NavDropdown label="Learn" items={LEARN_ITEMS} isOpen={openDropdown === "learn"} onToggle={() => toggle("learn")} onClose={close} dropdownRef={learnRef} />
          <Link to="/pricing" className="text-sm text-[#a3a3a3] hover:text-white no-underline font-medium">Pricing</Link>
          <button onClick={handleSignIn} className="flex items-center gap-2 px-4 py-2 bg-white text-black font-bold text-xs rounded-sm hover:bg-gray-100 transition-colors" data-testid="public-nav-signin">
            <GoogleLogo size={14} weight="bold" /> SIGN IN
          </button>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={handleSignIn} className="flex items-center gap-1.5 px-3 py-2 bg-white text-black font-bold text-[10px] rounded-sm" data-testid="public-nav-signin-mobile">
            <GoogleLogo size={12} weight="bold" /> SIGN IN
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-[#a3a3a3] hover:text-white" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#262626] bg-[#0a0a0a] px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] px-3 pt-1 pb-1">TOOLS</p>
          {TOOL_ITEMS.map(f => (
            <Link key={f.to} to={f.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 py-2.5 px-3 text-sm text-[#a3a3a3] hover:text-white no-underline rounded-sm transition-colors">
              <span className="text-[#ccff00]">{f.icon}</span> {f.label}
            </Link>
          ))}
          <div className="border-t border-[#262626] my-2" />
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] px-3 pt-1 pb-1">FEATURES</p>
          <Link to="/features" onClick={() => setMobileOpen(false)} className="block py-2.5 px-3 text-sm text-[#ccff00] no-underline font-bold">All Features Overview</Link>
          {FEATURE_ITEMS.map(f => (
            <Link key={f.to} to={f.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 py-2.5 px-3 text-sm text-[#a3a3a3] hover:text-white no-underline rounded-sm transition-colors">
              <span className="text-[#ccff00]">{f.icon}</span> {f.label}
            </Link>
          ))}
          <div className="border-t border-[#262626] my-2" />
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] px-3 pt-1 pb-1">LEARN</p>
          {LEARN_ITEMS.map(f => (
            <Link key={f.to} to={f.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5 py-2.5 px-3 text-sm text-[#a3a3a3] hover:text-white no-underline rounded-sm transition-colors">
              <span className="text-[#ccff00]">{f.icon}</span> {f.label}
            </Link>
          ))}
          <div className="border-t border-[#262626] my-2" />
          <Link to="/pricing" onClick={() => setMobileOpen(false)} className="block py-2.5 px-3 text-sm text-white no-underline font-bold">Pricing</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2.5 px-3 text-sm text-[#a3a3a3] no-underline">Contact / Support</Link>
        </div>
      )}
    </div>
  );
}
